import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/hooks/useAuthApi';
import { AlertCircle, Loader2 } from 'lucide-react';
import { validateInvitationToken } from '@/services/organizationService';
import { OrganizationInvitation } from '@/types/organization';
import AuthLayout from '@/components/auth/AuthLayout';
import InviteOnlyView from '@/components/auth/InviteOnlyView';
import { normalizeAuthErrorMessage } from '@/lib/authErrors';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

/**
 * Extract invitation token from URL search parameters
 * @param searchParams - URLSearchParams object
 * @returns The invitation token or undefined if not found
 */
const getInvitationToken = (searchParams: URLSearchParams): string | undefined => {
  // Try to get token directly from query parameter
  let token = searchParams.get('token');

  // Fallback: Extract token from returnTo parameter if not found directly
  if (!token) {
    const returnTo = searchParams.get('returnTo');
    if (returnTo) {
      const match = returnTo.match(/\/invite\/([^/?]+)/);
      if (match) {
        token = match[1];
      }
    }
  }

  return token || undefined;
};

const SignUp = () => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [validatingToken, setValidatingToken] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [invitation, setInvitation] = useState<OrganizationInvitation | null>(null);
  const { signUp, reinitializeAuth } = useAuth();
  const navigate = useNavigate();
  const context = 'signup';

  // Validate invitation token on page load
  useEffect(() => {
    const validateToken = async () => {
      const token = getInvitationToken(searchParams);

      if (!token) {
        setError('Please reach out to your organization admin or Hobbes contact for an invite link');
        setTokenValid(false);
        setValidatingToken(false);
        return;
      }

      try {
        const validation = await validateInvitationToken(token);
        if (validation.valid && validation.invitation) {
          setTokenValid(true);
          setInvitation(validation.invitation);
          // Pre-populate email from invitation
          setEmail(validation.invitation.email);
        } else {
          setError('This invitation link is invalid or has expired. Please reach out to your organization admin or Hobbes contact for an invite link');
          setTokenValid(false);
        }
      } catch (error) {
        console.error('Error validating token:', error);
        setError('Please reach out to your organization admin or Hobbes contact for an invite link');
        setTokenValid(false);
      } finally {
        setValidatingToken(false);
      }
    };

    validateToken();
  }, [searchParams]);

  // Pre-populate email from URL params (fallback)
  useEffect(() => {
    const emailParam = searchParams.get('email');
    if (emailParam && !invitation) {
      setEmail(decodeURIComponent(emailParam));
    }
  }, [searchParams, invitation]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Check if token is valid
    if (!tokenValid) {
      setError('Please reach out to your organization admin or Hobbes contact for an invite link');
      return;
    }

    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password strength
    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setIsLoading(true);

    try {
      const token = getInvitationToken(searchParams);
      await signUp(email, password, token);

      // Check for returnTo parameter to redirect back to invitation
      const returnTo = searchParams.get('returnTo');
      if (returnTo) {
        navigate(decodeURIComponent(returnTo));
      } else {
        navigate('/');
      }
    } catch (error: any) {
      setError(
        error.code === 'auth/email-already-in-use'
          ? 'Email already in use. Please try signing in instead.'
          : error.code === 'auth/invalid-email'
          ? 'Invalid email address'
          : error.code === 'auth/weak-password'
          ? 'Password is too weak. Please use a stronger password.'
          : normalizeAuthErrorMessage(error?.message, 'An error occurred. Please try again.')
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');

    // Check if token is valid
    if (!tokenValid) {
      setError('Please reach out to your organization admin or Hobbes contact for an invite link');
      return;
    }

    setIsLoading(true);
    const authMessageReceived = { current: false };

    try {
      const redirectUrl = `${window.location.origin}/auth/google/callback`;
      const token = getInvitationToken(searchParams);

      const response = await fetch(import.meta.env.VITE_API_BASE_URL + '/api/auth/google/url', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          redirect_url: redirectUrl,
          invitation_token: token,
          context: context,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to get OAuth URL');
      }

      const { url: authUrl } = await response.json();

      // Open popup for Google OAuth
      const popup = window.open(
        authUrl,
        'google_signin',
        'width=500,height=600,scrollbars=yes,resizable=yes'
      );

      if (!popup) {
        throw new Error('Popup blocked. Please allow popups and try again.');
      }

      // Full cleanup: clear all timers and remove listener (called lazily, so
      // forward-referencing checkClosed / messageListener / timeoutId is safe)
      const cleanup = () => {
        clearInterval(checkClosed);
        clearTimeout(timeoutId);
        window.removeEventListener('message', messageListener);
      };

      const checkClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkClosed);
          clearTimeout(timeoutId);
          // Give late-arriving postMessage 500ms to be delivered before
          // concluding that auth was cancelled and cleaning up the listener.
          setTimeout(() => {
            if (!authMessageReceived.current) {
              window.removeEventListener('message', messageListener);
              setError('Google sign-in was cancelled.');
              setIsLoading(false);
            }
          }, 500);
        }
      }, 1000);

      const messageListener = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;

        if (event.data.type === 'GOOGLE_AUTH_SUCCESS') {
          authMessageReceived.current = true;
          cleanup();
          popup.close();
          setError('');

          handleGoogleAuthSuccess(event.data.code, event.data.state, redirectUrl);
        } else if (event.data.type === 'GOOGLE_AUTH_ERROR') {
          authMessageReceived.current = true;
          cleanup();
          popup.close();

          setError(event.data.error || 'Google sign-in failed.');
          setIsLoading(false);
        }
      };

      window.addEventListener('message', messageListener);

      const timeoutId = setTimeout(() => {
        cleanup();
        if (!popup.closed) {
          popup.close();
        }
        if (!authMessageReceived.current) {
          setError('Google sign-in timed out. Please try again.');
          setIsLoading(false);
        }
      }, 300000);
    } catch (error: any) {
      console.error('Google sign-in error:', error);
      setError(normalizeAuthErrorMessage(error?.message, 'Could not sign up with Google. Please try again.'));
      setIsLoading(false);
    }
  };

  const handleGoogleAuthSuccess = async (authCode: string, state: string, redirectUri: string) => {
    try {
      const response = await fetch(import.meta.env.VITE_API_BASE_URL + '/api/auth/google/callback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code: authCode,
          state: state,
          context: context,
          redirect_uri: redirectUri,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to authenticate with Google');
      }

      if (data.access_token && data.user) {
        localStorage.setItem('hobbes_auth_token', data.access_token);
        await reinitializeAuth();

        const returnTo = searchParams.get('returnTo');
        if (returnTo) {
          navigate(decodeURIComponent(returnTo));
        } else {
          navigate('/');
        }
      } else {
        throw new Error('Invalid authentication response');
      }
    } catch (error: any) {
      console.error('Google auth callback error:', error);
      setError(normalizeAuthErrorMessage(error?.message, 'Authentication failed. Please try again.'));
      setIsLoading(false);
    }
  };

  const authInputClass = 'h-10 rounded-[10px] border-white/10 bg-transparent text-white text-base tracking-[-0.16px] placeholder:text-[#b2b2b2] focus-visible:ring-white/30 focus-visible:ring-offset-0';

  // Show loading spinner while validating token
  if (validatingToken) {
    return (
      <AuthLayout title="Create an account" pageTitle="Sign Up" showTermsLinks={false}>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-white" />
        </div>
      </AuthLayout>
    );
  }

  // Show invite-only view when token is invalid or missing
  if (!tokenValid) {
    return (
      <AuthLayout title="Invite Only" pageTitle="Invite Only" showTermsLinks={false}>
        <InviteOnlyView source="signup_page" showSignInLink={true} />
      </AuthLayout>
    );
  }

  // Normal signup form for valid invitation tokens
  const isDisabled = isLoading;

  return (
    <AuthLayout
      title="Create an account"
      pageTitle="Sign Up"
      showTermsLinks={true}
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-6">
        <AnimatePresence mode="wait">
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="bg-red-500/20 border border-red-500/50 text-red-200 p-3 rounded-xl flex items-start gap-2"
            >
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span className="text-sm">{error}</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Email field */}
        <div className="flex flex-col gap-2">
          <label htmlFor="email" className="text-sm text-[#a4a4a4] tracking-[-0.14px]">
            Email
          </label>
          <Input
            id="email"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isDisabled}
            required
            className={authInputClass}
          />
        </div>

        {/* Password field */}
        <div className="flex flex-col gap-2">
          <label htmlFor="password" className="text-sm text-[#a4a4a4] tracking-[-0.14px]">
            Password
          </label>
          <Input
            id="password"
            type="password"
            placeholder="Create a password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={isDisabled}
            required
            className={authInputClass}
          />
        </div>

        {/* Confirm Password field */}
        <div className="flex flex-col gap-2">
          <label htmlFor="confirmPassword" className="text-sm text-[#a4a4a4] tracking-[-0.14px]">
            Confirm Password
          </label>
          <Input
            id="confirmPassword"
            type="password"
            placeholder="Confirm your password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={isDisabled}
            required
            className={authInputClass}
          />
        </div>

        {/* Create account button */}
        <Button
          type="submit"
          disabled={isDisabled}
          className="w-full h-10 bg-white text-[#404040] font-medium rounded-[36px] hover:bg-white/90 text-base tracking-[-0.16px]"
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Creating account...
            </span>
          ) : (
            'Create Account'
          )}
        </Button>

        {/* Divider */}
        <div className="flex items-center gap-4">
          <div className="flex-1 h-px bg-white/10" />
          <span className="text-xs text-[#a4a4a4] tracking-[-0.12px]">Or continue with</span>
          <div className="flex-1 h-px bg-white/10" />
        </div>

        {/* Google button */}
        <Button
          type="button"
          variant="outline"
          onClick={handleGoogleSignIn}
          disabled={isDisabled}
          className="w-full h-10 bg-white/10 border-white/10 text-[#e6e5e5] font-medium rounded-[36px] hover:bg-white/20 hover:text-white text-base tracking-[-0.16px]"
        >
          <svg className="h-5 w-5" viewBox="0 0 24 24">
            <path
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              fill="#4285F4"
            />
            <path
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              fill="#34A853"
            />
            <path
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              fill="#FBBC05"
            />
            <path
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              fill="#EA4335"
            />
            <path d="M1 1h22v22H1z" fill="none" />
          </svg>
          Google
        </Button>

        <div className="text-center text-sm text-[#a4a4a4] tracking-[-0.14px]">
          Already have an account?{' '}
          <Link to="/login" className="text-white underline underline-offset-4 hover:text-white/80">
            Sign in
          </Link>
        </div>
      </form>
    </AuthLayout>
  );
};

export default SignUp;
